<template>
	<app-panel
		v-if="depot"
		:key="keyValue"
		class="min-w-min grow overflow-hidden h-80 relative"
		:title="title"
		:help="help"
		compact
		scroll-listener
		loading-overlay
		:loading="loading"
		@scrolled="getData(true)">
		<template #header>
			<div class="flex items-center gap-2">
				<app-button v-if="viewAll" :to="viewAll" size="x-small" theme="outline">
					View all
				</app-button>

				<div class="flex items-center">
					<app-dropdown
						v-model="status"
						:options="statusOptions"
						size="x-small"
						:theme="status ? 'primary' : 'outline'"
						align="right"
						:input-group="area == 'pick' ? 'left' : undefined"
						button
						@update:model-value="getData()">
						<app-icon :icon="['fas', 'fa-filter']" />

						<template #empty> All </template>
					</app-dropdown>

					<button
						v-if="area == 'pick'"
						class="ring-gray-300 ring-1 rounded-e text-[11px] h-[23px] px-2 cursor-pointer"
						@click="loadFilter = !loadFilter">
						{{ loadFilter ? 'Load' : 'Pick' }}
					</button>
				</div>

				<div v-if="area === 'stock-check'">
					<app-button theme="primary" size="x-small" @click="toggleDropdown">
						<app-icon :icon="['fas', 'fa-warehouse']" />
					</app-button>

					<div
						v-if="showDropdown"
						class="absolute mt-2 min-w-[160px] max-w-sm rounded shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10 right-0 p-3 text-[11px] flex items-center gap-2 text-gray-900">
						<span>Display other depots</span>
						<app-checkbox
							v-model="withSecondary"
							name="withSecondary"
							wrapper=""
							:true-value="true"
							:false-value="false"
							@update:model-value="((showDropdown = false), getData())" />
					</div>
				</div>
				<status-descriptions
					:title="`${title} Status Key`"
					:area="area"
					workspace="hire-desk" />
			</div>
		</template>

		<workspace-table
			:items
			:route
			:area
			:params
			:total
			:status-key="contractItemStatusMap[area]"
			@params-changed="(newParams: Params) => getData(false, newParams)" />
	</app-panel>
</template>

<script lang="ts" setup>
	import { ref, watch, onMounted } from 'vue'
	import WorkspaceTable from '@/components/datatables/all/WorkspaceTable.vue'
	import useToasterStore from '@/stores/toaster'
	import ContractService from '@/services/contract'
	import { CollectionContract } from '@/data/schema/contract'
	import { formatContractDates } from '@/utils/date'
	import type { WorkspaceArea } from '@/data/types/area'
	import { contractItemStatusMap } from '@/utils/statusMaps'
	import { useContractStatus } from '@/composables/status'
	import StatusDescriptions from '../cat2/StatusDescriptions.vue'
	import type { Params } from '@/data/types/datatable'

	const props = defineProps({
		refreshTrigger: {
			type: Number,
			default: 0
		},
		depot: {
			type: String,
			default: null
		},
		area: {
			type: String as () => WorkspaceArea,
			default: 'stock-check'
		},
		title: {
			type: String,
			default: undefined
		},
		help: {
			type: String,
			default: undefined
		},
		route: {
			type: String,
			default: undefined
		},
		viewAll: {
			type: String,
			default: undefined
		}
	})

	// Resources
	const api = new ContractService()
	const toaster = useToasterStore()

	// Data
	const keyValue = ref(0)
	const items = ref<CollectionContract[]>([])
	const loading = ref(true)
	const page = ref(1)
	const last = ref(false)
	const withSecondary = ref(false)
	const showDropdown = ref(false)

	const status = ref()
	const { statusOptions } = useContractStatus(null)
	const loadFilter = ref(false)

	const total = ref(10)
	const params = ref<Params>({
		page: 1,
		rowsPerPage: 10,
		sortBy: undefined,
		sortType: 'asc'
	})

	const getData = (nextPage = false, newParams?: Params) => {
		if (newParams) {
			// update our params with the ones emitted from the table
			params.value = newParams
		}

		if (nextPage) {
			page.value++
		} else {
			// Force re-render with key iteration
			keyValue.value++
			page.value = 1
			last.value = false
		}

		// Handle sort
		let sort = null
		if (params.value && params.value.sortBy && params.value.sortType) {
			sort = params.value.sortBy + '|' + (params.value.sortType as string).toUpperCase()
		}

		if (!last.value) {
			loading.value = true

			api.getHireDeskArea(props.area, props.depot, page.value, 10, sort, {
				status: (!loadFilter.value ? status.value : undefined) ?? null,
				loadStatus: loadFilter.value ? status.value : undefined,
				withSecondary: withSecondary.value
			})
				.then((res) => {
					if (
						res.data.data.length > 0 &&
						res.data.meta.paging.current_page == res.data.meta.paging.last_page
					) {
						last.value = true
					}

					if (page.value == 1) {
						items.value = formatContractDates(res.data.data)
					} else {
						let page = formatContractDates(res.data.data)
						items.value.push(...page)
					}

					total.value = res.data.meta.total
					loading.value = false
				})
				.catch((error) => {
					console.error(error)
					toaster.error({
						text: 'An error occurred retrieving contract data'
					})
					loading.value = false
				})
		}
	}

	const toggleDropdown = () => {
		showDropdown.value = !showDropdown.value
	}

	watch(
		() => props.depot,
		() => {
			if (props.depot !== null) {
				getData()
			}
		},
		{ deep: true }
	)

	watch(
		() => props.refreshTrigger,
		(newValue, oldValue) => {
			if (newValue !== oldValue) {
				getData()
			}
		}
	)

	watch(
		() => loadFilter.value,
		() => {
			if (props.depot && status.value) {
				getData()
			}
		}
	)

	onMounted(() => {
		getData()
	})
</script>
